import { Component, Input, Output, EventEmitter } from '@angular/core';
import { AgentConfig } from '../../models';
import { LoggerService } from '../../../../services/logger-services/logger.service';

/**
 * Agent Selector Component
 * 
 * Displays list of available agents for selection.
 * Shows agent icons and names in a clickable list.
 * 
 * Features:
 * - Lists all available agents
 * - Highlights currently active agent
 * - Emits selection events to parent
 * - Prevents re-selection of already active agent
 * 
 * Usage:
 * <app-agent-selector
 *   [availableAgents]="agentList"
 *   [currentAgentId]="activeAgentId"
 *   (agentSelected)="onAgentChange($event)">
 * </app-agent-selector>
 */
@Component({
  selector: 'app-agent-selector',
  templateUrl: './agent-selector.component.html',
  styleUrls: ['./agent-selector.component.scss']
})
export class AgentSelectorComponent {
  @Input() availableAgents: AgentConfig[] = [];
  @Input() currentAgentId: string = "module";
  @Output() agentSelected = new EventEmitter<AgentConfig>();

  constructor(private logger: LoggerService) {}

  /**
   * Handle agent selection from list
   * Prevents re-selection of currently active agent
   * 
   * @param agent - Selected agent configuration
   */
  selectAgent(agent: AgentConfig): void {
    try {
      if (this.isActiveAgent(agent.id)) {
        return;
      }
      
      this.currentAgentId = agent.id;
      this.agentSelected.emit(agent);
    } catch (error: any) {
      this.logger.logInfo('[AgentSelector] Error selecting agent', 'selectAgent', error);
    }
  }

  /**
   * Get count of available agents
   * 
   * @returns number - Number of available agents
   */
  get agentCount(): number {
    try {
      return this.availableAgents.length;
    } catch (error: any) {
      this.logger.logInfo('[AgentSelector] Error getting agent count', 'agentCount', error);
      return 0;
    }
  }

  /**
   * Check if agent is currently active
   * 
   * @param agentId - ID of agent to check
   * @returns boolean - True if agent is active
   */
  isActiveAgent(agentId: string): boolean {
    try {
      return this.currentAgentId === agentId;
    } catch (error: any) {
      this.logger.logInfo('[AgentSelector] Error checking active agent', 'isActiveAgent', error);
      return false;
    }
  }

  /**
   * Get currently active agent configuration
   * 
   * @returns AgentConfig | undefined - Active agent or undefined
   */
  getCurrentAgent(): AgentConfig | undefined {
    try {
      return this.availableAgents.find(a => a.id === this.currentAgentId);
    } catch (error: any) {
      this.logger.logInfo('[AgentSelector] Error getting current agent', 'getCurrentAgent', error);
      return undefined;
    }
  }
}
